import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiGet } from "../../services/api/apiClient";

interface AssetsState {
  assets: any[];
  loading: boolean;
  error: string | null;
}

const initialState: AssetsState = {
  assets: [],
  loading: false,
  error: null,
};

export const getProjectAssets = createAsyncThunk(
  "assets/getProjectAssets",
  async (projectId: string, { rejectWithValue, getState, signal }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      if (!user) {
        return rejectWithValue("User not logged in");
      }

      const result = await apiGet({
        endpoint: `/get-project-assets?projectId=${projectId}`,
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });

      return result.data;
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

export const getProjectsAssets = createAsyncThunk(
  "assets/getProjectsAssets",
  async (_, { rejectWithValue, getState, signal }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      if (!user) {
        return rejectWithValue("User not logged in");
      }

      const result = await apiGet({
        endpoint: `/get-projects-assets?userId=${user.id}`,
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });

      return result.data;
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

const assetsSlice = createSlice({
  name: "assets",
  initialState,
  reducers: {
    resetAssets: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProjectsAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectsAssets.fulfilled, (state, action) => {
        state.loading = false;
        state.assets = action.payload || [];
      })
      .addCase(getProjectsAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getProjectAssets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProjectAssets.fulfilled, (state, action) => {
        state.loading = false;
        state.assets = action.payload || [];
      })
      .addCase(getProjectAssets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetAssets } = assetsSlice.actions;

export default assetsSlice.reducer;
